import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contabilidadService } from '../services/contabilidadService';
import type { CuentaBancaria, CreateCajaPayload } from '../types';

export const CAJAS_QUERY_KEY = ['contabilidad', 'cajas'] as const;

export function useCajas() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<CuentaBancaria[]>({
    queryKey: CAJAS_QUERY_KEY,
    queryFn: () => contabilidadService.getCajas(),
    staleTime: 1000 * 60 * 5,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateCajaPayload) => contabilidadService.createCaja(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CAJAS_QUERY_KEY }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<CreateCajaPayload> }) =>
      contabilidadService.updateCaja(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CAJAS_QUERY_KEY }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => contabilidadService.deleteCaja(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CAJAS_QUERY_KEY }),
  });

  return {
    cajas: data ?? [],
    isLoading,
    error,
    createCaja: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateCaja: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteCaja: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
